import { useEffect, useMemo } from 'react';
import { Icon } from '@iconify/react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { getVendorPaymentDetails } from '../../../redux/actions/SocietyPortal/VendorAction/VendorAction';
import * as routes from '../../../Routing/routes';
import { getPaymentTypeLabel } from '../../../utils/helpers/paymentType';

const ViewVendorPayment = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const isPrint = searchParams.get('print') === 'true';

    const { vendorPaymentDetailsData, vendorPaymentDetailsLoading } = useSelector(state => state.VendorReducers);

    useEffect(() => {
        dispatch(getVendorPaymentDetails({ paymentId: id }));
    }, [dispatch, id]);

    // Auto-trigger print
    useEffect(() => {
        if (isPrint && vendorPaymentDetailsData && !vendorPaymentDetailsLoading) {
            const timer = setTimeout(() => window.print(), 500);
            return () => clearTimeout(timer);
        }
    }, [isPrint, vendorPaymentDetailsData, vendorPaymentDetailsLoading]);

    const paymentData = useMemo(() => {
        if (!vendorPaymentDetailsData) return null;
        const d = vendorPaymentDetailsData;
        const details = d.details || [];
        const totalBillPayment = details.reduce((sum, det) => sum + parseFloat(det.amount || 0), 0);

        return {
            id: d.payment_id || d.id,
            paymentNo: d.payment_no || '',
            vendorName: d.vendor_name || d.vendor?.business_name || 'N/A',
            vendorGlCode: d.vendor_account || d.vendor?.vendor_gl_code || '',
            vendorGst: d.vendor?.gst_number || '',
            vendorPan: d.vendor?.pan_number || '',
            paymentType: d.payment_type || '',
            paymentDate: d.payment_date,
            paymentMode: d.payment_mode || '',
            reference: d.reference || '',
            status: d.generate_status || d.status || 'Draft',
            notes: d.notes || '',
            amount: parseFloat(d.payment_amount || 0),
            // What actually left the bank: the gross less any TDS withheld.
            netAmount: parseFloat(d.payment_amount || 0)
                - (d.tds_applicable === 'Y' ? parseFloat(d.tds_amount || 0) : 0),
            tdsApplicable: d.tds_applicable || 'N',
            tdsRate: parseFloat(d.tds_rate || 0),
            tdsAmount: parseFloat(d.tds_amount || 0),
            tdsSection: d.tds_section || '',
            debitAccount: d.debit_account || '',
            society: d.society || {},
            details: details,
            totalBillPayment,
            excessAmount: parseFloat(d.payment_amount || 0) - totalBillPayment,
        };
    }, [vendorPaymentDetailsData]);

    const formatCurrency = (amount) => {
        const val = parseFloat(amount || 0);
        return val.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const formatDate = (date) => {
        if (!date) return '-';
        return new Date(date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    };

    const numberToWords = (num) => {
        if (num === 0) return 'Zero';
        const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
            'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
        const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
        const convertLessThanThousand = (n) => {
            if (n === 0) return '';
            if (n < 20) return ones[n];
            if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
            return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' and ' + convertLessThanThousand(n % 100) : '');
        };
        const absNum = Math.abs(Math.round(num));
        if (absNum === 0) return 'Zero';
        let result = '';
        const crore = Math.floor(absNum / 10000000);
        const lakh = Math.floor((absNum % 10000000) / 100000);
        const thousand = Math.floor((absNum % 100000) / 1000);
        const remainder = absNum % 1000;
        if (crore) result += convertLessThanThousand(crore) + ' Crore ';
        if (lakh) result += convertLessThanThousand(lakh) + ' Lakh ';
        if (thousand) result += convertLessThanThousand(thousand) + ' Thousand ';
        if (remainder) result += convertLessThanThousand(remainder);
        return (num < 0 ? 'Minus ' : '') + result.trim() + ' Rupees Only';
    };

    const getStatusStyles = (status) => {
        const s = status?.toLowerCase();
        if (s === 'posted' || s === 'completed') return { background: '#d1fae5', color: '#065f46' };
        if (s === 'draft') return { background: '#fef3c7', color: '#92400e' };
        if (s === 'failed' || s === 'cancelled') return { background: '#fecaca', color: '#991b1b' };
        return { background: '#e5e7eb', color: '#374151' };
    };

    const getPaymentModeLabel = (mode) => {
        const labels = { cash: 'Cash', cheque: 'Cheque', neft: 'NEFT' };
        return labels[mode] || mode;
    };

    const handlePrint = () => window.print();
    const handleBack = () => navigate('/society-portal' + routes.SOCIETY_PORTAL_VENDOR_PAYMENTS);

    if (vendorPaymentDetailsLoading) {
        return (
            <div className="tw-min-h-screen tw-flex tw-items-center tw-justify-center tw-bg-gray-50">
                <div className="tw-text-center">
                    <div className="tw-animate-spin tw-rounded-full tw-h-12 tw-w-12 tw-border-b-2 tw-border-indigo-600 tw-mx-auto"></div>
                    <p className="tw-text-gray-500 tw-mt-4 tw-text-sm">Loading payment details...</p>
                </div>
            </div>
        );
    }

    if (!paymentData) {
        return (
            <div className="tw-flex tw-items-center tw-justify-center tw-min-h-[400px]">
                <div className="tw-flex tw-flex-col tw-items-center tw-gap-3">
                    <Icon icon="mdi:cash-remove" className="tw-text-4xl tw-text-red-400" />
                    <p className="tw-text-gray-600 tw-font-medium">Payment not found</p>
                    <button onClick={handleBack} className="tw-text-blue-600 hover:tw-underline tw-text-sm">
                        Back to Payments
                    </button>
                </div>
            </div>
        );
    }

    const statusStyles = getStatusStyles(paymentData.status);
    const society = paymentData.society || {};
    const primaryAddress = society.addresses?.find(a => a.is_primary === 1) || society.addresses?.[0] || {};
    const societyAddress = [
        primaryAddress.address_line1,
        primaryAddress.address_line2,
        [primaryAddress.city?.name, primaryAddress.state?.name].filter(Boolean).join(', '),
        primaryAddress.pincode ? `- ${primaryAddress.pincode}` : ''
    ].filter(Boolean).join(', ');
    const primaryContact = society.contacts?.find(c => c.is_primary === 1) || society.contacts?.[0] || {};
    const billDetails = Array.isArray(paymentData.details) ? paymentData.details : [];

    return (
        <>
            {/* Print-specific styles */}
            <style>{`
                @media print {
                    body { margin: 0; padding: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                    .no-print { display: none !important; }
                    .print-page { margin: 0; padding: 6mm; box-shadow: none !important; border: none !important; }
                    @page { size: A4; margin: 8mm; }
                }
            `}</style>

            {/* Action Bar - hidden in print */}
            <div className="no-print tw-bg-white tw-border-b tw-border-gray-200 tw-sticky tw-top-0 tw-z-10 tw-shadow-sm">
                <div className="tw-max-w-4xl tw-mx-auto tw-px-6 tw-py-3 tw-flex tw-items-center tw-justify-between">
                    <div className="tw-flex tw-items-center tw-gap-3">
                        <button onClick={handleBack} className="tw-p-2 tw-rounded-lg hover:tw-bg-gray-100 tw-text-gray-500 tw-transition-colors">
                            <Icon icon="mdi:arrow-left" className="tw-text-xl" />
                        </button>
                        <div>
                            <h1 className="tw-text-lg tw-font-bold tw-text-gray-900">Vendor Payment</h1>
                            <p className="tw-text-xs tw-text-gray-500">{paymentData.paymentNo}</p>
                        </div>
                    </div>
                    <div className="tw-flex tw-items-center tw-gap-3">
                        <button
                            onClick={handleBack}
                            className="tw-px-4 tw-py-2 tw-bg-white tw-border tw-border-gray-300 tw-rounded-lg tw-text-sm tw-font-medium tw-text-gray-700 hover:tw-bg-gray-50 tw-transition-colors"
                        >
                            Back
                        </button>
                        <button
                            onClick={() => navigate('/society-portal' + routes.SOCIETY_PORTAL_VENDOR_PAYMENTS_EDIT.replace(':id', paymentData.id))}
                            className="tw-px-4 tw-py-2 tw-bg-white tw-border tw-border-gray-300 tw-rounded-lg tw-text-sm tw-font-medium tw-text-gray-700 hover:tw-bg-gray-50 tw-transition-colors tw-flex tw-items-center tw-gap-1.5"
                        >
                            <Icon icon="mdi:pencil" /> Edit
                        </button>
                        <button
                            onClick={handlePrint}
                            className="tw-flex tw-items-center tw-gap-2 tw-px-5 tw-py-2 tw-bg-indigo-600 tw-text-white tw-rounded-lg tw-text-sm tw-font-semibold hover:tw-bg-indigo-700 tw-transition-colors tw-shadow-sm"
                        >
                            <Icon icon="mdi:printer" className="tw-text-lg" /> Print
                        </button>
                    </div>
                </div>
            </div>

            {/* Print Layout */}
            <div className="tw-bg-gray-100 tw-min-h-screen tw-py-8 no-print-bg">
                <div className="print-page tw-max-w-4xl tw-mx-auto tw-bg-white tw-shadow-lg tw-border tw-border-gray-200" style={{ fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif", fontSize: '12px' }}>

                    {/* Header */}
                    <div style={{ borderBottom: '3px solid #4338ca', padding: '20px 28px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div style={{ maxWidth: '60%' }}>
                            <h1 style={{ fontSize: '22px', fontWeight: 700, color: '#1e1b4b', margin: 0, letterSpacing: '-0.3px' }}>
                                Payment Voucher
                            </h1>
                            {society.name && (
                                <p style={{ fontSize: '14px', color: '#4338ca', fontWeight: 700, margin: '4px 0 0 0' }}>
                                    {society.name}
                                </p>
                            )}
                            {societyAddress && (
                                <p style={{ fontSize: '10px', color: '#6b7280', margin: '4px 0 0 0', lineHeight: '1.4' }}>
                                    {societyAddress}
                                </p>
                            )}
                            {primaryContact.phone && (
                                <p style={{ fontSize: '10px', color: '#6b7280', margin: '2px 0 0 0' }}>
                                    Tel: {primaryContact.phone} {primaryContact.email ? `| ${primaryContact.email}` : ''}
                                </p>
                            )}
                            {society.registration_number && (
                                <p style={{ fontSize: '10px', color: '#6b7280', margin: '2px 0 0 0' }}>
                                    Reg No: {society.registration_number}
                                </p>
                            )}
                        </div>
                        <div style={{ textAlign: 'right' }}>
                            <div style={{ fontSize: '10px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600 }}>
                                Payment No.
                            </div>
                            <div style={{ fontSize: '16px', fontWeight: 700, color: '#1e1b4b', margin: '2px 0' }}>
                                {paymentData.paymentNo}
                            </div>
                            <div style={{
                                display: 'inline-block',
                                padding: '3px 12px',
                                borderRadius: '12px',
                                fontSize: '10px',
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                ...statusStyles
                            }}>
                                {paymentData.status}
                            </div>
                        </div>
                    </div>

                    {/* Payment Info Row */}
                    <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb', fontSize: '11px' }}>
                        <div style={{ flex: 1, padding: '10px 28px', borderRight: '1px solid #e5e7eb' }}>
                            <span style={{ color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', fontSize: '9px', letterSpacing: '0.5px' }}>Payment Date</span>
                            <div style={{ fontWeight: 600, color: '#1f2937', marginTop: '2px' }}>{formatDate(paymentData.paymentDate)}</div>
                        </div>
                        <div style={{ flex: 1, padding: '10px 28px', borderRight: '1px solid #e5e7eb' }}>
                            <span style={{ color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', fontSize: '9px', letterSpacing: '0.5px' }}>Payment Mode</span>
                            <div style={{ fontWeight: 600, color: '#1f2937', marginTop: '2px' }}>{getPaymentModeLabel(paymentData.paymentMode)}</div>
                        </div>
                        <div style={{ flex: 1, padding: '10px 28px', borderRight: '1px solid #e5e7eb' }}>
                            <span style={{ color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', fontSize: '9px', letterSpacing: '0.5px' }}>Payment Type</span>
                            <div style={{ fontWeight: 600, color: '#1f2937', marginTop: '2px' }}>{getPaymentTypeLabel(paymentData.paymentType)}</div>
                        </div>
                        <div style={{ flex: 1, padding: '10px 28px' }}>
                            <span style={{ color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', fontSize: '9px', letterSpacing: '0.5px' }}>UTR/Cheque No</span>
                            <div style={{ fontWeight: 600, color: '#1f2937', marginTop: '2px' }}>{paymentData.reference || '-'}</div>
                        </div>
                    </div>

                    {/* Vendor Info Row */}
                    <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb', fontSize: '11px' }}>
                        <div style={{ flex: 2, padding: '10px 28px', borderRight: '1px solid #e5e7eb' }}>
                            <span style={{ color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', fontSize: '9px', letterSpacing: '0.5px' }}>Paid To (Vendor)</span>
                            <div style={{ fontWeight: 700, color: '#1f2937', marginTop: '2px', fontSize: '13px' }}>{paymentData.vendorName}</div>
                            {paymentData.vendorGlCode && <div style={{ fontSize: '10px', color: '#6b7280', marginTop: '1px' }}>GL Code: {paymentData.vendorGlCode}</div>}
                        </div>
                        {paymentData.vendorPan && (
                            <div style={{ flex: 1, padding: '10px 28px', borderRight: '1px solid #e5e7eb' }}>
                                <span style={{ color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', fontSize: '9px', letterSpacing: '0.5px' }}>PAN</span>
                                <div style={{ fontWeight: 600, color: '#1f2937', marginTop: '2px' }}>{paymentData.vendorPan}</div>
                            </div>
                        )}
                        {paymentData.vendorGst && (
                            <div style={{ flex: 1, padding: '10px 28px' }}>
                                <span style={{ color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', fontSize: '9px', letterSpacing: '0.5px' }}>GSTIN</span>
                                <div style={{ fontWeight: 600, color: '#1f2937', marginTop: '2px' }}>{paymentData.vendorGst}</div>
                            </div>
                        )}
                    </div>

                    {/* Bills Applied Table */}
                    {billDetails.length > 0 && (
                        <div style={{ padding: '0' }}>
                            <div style={{ padding: '12px 28px 6px', fontSize: '11px', fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                Bills Applied
                            </div>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                                <thead>
                                    <tr style={{ background: '#f8fafc' }}>
                                        <th style={{ textAlign: 'left', padding: '8px 28px', fontWeight: 700, color: '#64748b', fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #e2e8f0', width: '30px' }}>#</th>
                                        <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 700, color: '#64748b', fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #e2e8f0' }}>Bill No</th>
                                        <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 700, color: '#64748b', fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #e2e8f0' }}>Bill Date</th>
                                        <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 700, color: '#64748b', fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #e2e8f0' }}>Bill Amount</th>
                                        <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 700, color: '#64748b', fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #e2e8f0' }}>Balance</th>
                                        <th style={{ textAlign: 'right', padding: '8px 28px', fontWeight: 700, color: '#64748b', fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #e2e8f0', width: '100px' }}>Payment</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {billDetails.map((det, idx) => (
                                        <tr key={det.payment_detail_id || idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                            <td style={{ padding: '8px 28px', color: '#9ca3af' }}>{idx + 1}</td>
                                            <td style={{ padding: '8px 12px', fontWeight: 600, color: '#1f2937' }}>
                                                {det.bill?.bill_number || '-'}
                                            </td>
                                            <td style={{ padding: '8px 12px', color: '#6b7280' }}>
                                                {formatDate(det.bill?.bill_date || det.payment_date)}
                                            </td>
                                            <td style={{ padding: '8px 12px', textAlign: 'right', color: '#374151' }}>
                                                {formatCurrency(det.bill?.total_amount)}
                                            </td>
                                            <td style={{ padding: '8px 12px', textAlign: 'right', color: '#dc2626', fontWeight: 600 }}>
                                                {formatCurrency(det.bill?.balance_amount)}
                                            </td>
                                            <td style={{ padding: '8px 28px', textAlign: 'right', fontWeight: 700, color: '#1f2937' }}>
                                                {formatCurrency(det.amount)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Summary Section */}
                    <div style={{ borderTop: '2px solid #e2e8f0', display: 'flex' }}>
                        {/* Left: Amount in words + Notes */}
                        <div style={{ flex: 1, padding: '14px 28px', borderRight: '1px solid #e5e7eb' }}>
                            <div style={{ fontSize: '9px', color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>
                                Amount in Words
                            </div>
                            <div style={{ fontSize: '11px', fontWeight: 600, color: '#374151', fontStyle: 'italic' }}>
                                {numberToWords(paymentData.netAmount ?? paymentData.amount)}
                            </div>

                            {paymentData.notes && (
                                <div style={{ marginTop: '10px' }}>
                                    <div style={{ fontSize: '9px', color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '3px' }}>
                                        Notes
                                    </div>
                                    <div style={{ fontSize: '10px', color: '#6b7280', lineHeight: '1.4' }}>
                                        {paymentData.notes}
                                    </div>
                                </div>
                            )}

                            {/* Signature area */}
                            <div style={{ marginTop: '40px', display: 'flex', justifyContent: 'space-between' }}>
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{ borderTop: '1px solid #d1d5db', width: '150px', paddingTop: '6px' }}>
                                        <span style={{ fontSize: '9px', color: '#9ca3af', fontWeight: 600 }}>Prepared By</span>
                                    </div>
                                </div>
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{ borderTop: '1px solid #d1d5db', width: '150px', paddingTop: '6px' }}>
                                        <span style={{ fontSize: '9px', color: '#9ca3af', fontWeight: 600 }}>Authorized Signatory</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Right: Amounts */}
                        <div style={{ width: '260px', padding: '14px 28px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: '11px' }}>
                                <span style={{ color: '#6b7280' }}>Amount Paid</span>
                                <span style={{ fontWeight: 600, color: '#374151' }}>Rs {formatCurrency(paymentData.amount)}</span>
                            </div>

                            {billDetails.length > 0 && (
                                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: '11px' }}>
                                    <span style={{ color: '#6b7280' }}>Applied to Bills</span>
                                    <span style={{ fontWeight: 600, color: '#374151' }}>Rs {formatCurrency(paymentData.totalBillPayment)}</span>
                                </div>
                            )}

                            {paymentData.tdsApplicable === 'Y' && paymentData.tdsAmount > 0 && (
                                <>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: '11px' }}>
                                        <span style={{ color: '#6b7280' }}>TDS ({paymentData.tdsRate}%){paymentData.tdsSection ? ` - ${paymentData.tdsSection}` : ''}</span>
                                        <span style={{ fontWeight: 600, color: '#dc2626' }}>- Rs {formatCurrency(paymentData.tdsAmount)}</span>
                                    </div>
                                </>
                            )}

                            {billDetails.length > 0 && paymentData.excessAmount !== 0 && (
                                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: '11px' }}>
                                    <span style={{ color: '#6b7280' }}>{paymentData.excessAmount >= 0 ? 'Excess Amount' : 'Short Amount'}</span>
                                    <span style={{ fontWeight: 600, color: paymentData.excessAmount >= 0 ? '#059669' : '#dc2626' }}>
                                        Rs {formatCurrency(Math.abs(paymentData.excessAmount))}
                                    </span>
                                </div>
                            )}

                            {/* Total */}
                            {/* Net of TDS: the amount that actually left the bank.
                                This printed the gross amount, so Amount Paid and
                                Net Payment always matched even with TDS deducted. */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0 4px 0', fontSize: '13px', borderTop: '2px solid #1e1b4b', marginTop: '6px' }}>
                                <span style={{ fontWeight: 700, color: '#1e1b4b' }}>Net Payment</span>
                                <span style={{ fontWeight: 800, color: '#1e1b4b' }}>
                                    Rs {formatCurrency(Math.max(0, paymentData.netAmount ?? paymentData.amount ?? 0))}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Footer */}
                    <div style={{ borderTop: '1px solid #e5e7eb', padding: '10px 28px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc' }}>
                        <div style={{ fontSize: '9px', color: '#9ca3af' }}>
                            Generated on {new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </div>
                        <div style={{ fontSize: '9px', color: '#9ca3af' }}>
                            {society.name ? `${society.name} | ` : ''}Payment Voucher | {paymentData.paymentNo}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ViewVendorPayment;
